import evessioAPI from "@/services/api/evessioAPI";
import awsRestAPI from "@/services/api/awsRestAPI";
import { CONTENT_TYPE_SLUGS, VIDEO_CHAT_TYPES } from "@/helpers/constants";

export default {
  /**
   * Update DigitalEventRoom (Exclude DigitalEventTable children)
   * @param state {Object}
   * @param commit {Function}
   * @param dispatch {Function}
   * @param state {Object}
   * @param uuid {String} ContentNode.uuid (DigitalEventRoom CT)
   * @return {Promise<{}>}
   */
  async updateDigitalEventRoom({ commit, getters, dispatch, rootState }, { uuid }) {
    let response = await evessioAPI.getContentNodeContext({
      uuid: uuid,
      includeChildren: 1,
      exclude: [CONTENT_TYPE_SLUGS.DIGITAL_EVENT_TABLE]
    });

    let room = getters.room(uuid);
    if (room && room.VIDEO_CHATS_ENABLED !== response.data.VIDEO_CHATS_ENABLED) {
      await dispatch("videoChat/toggleVideoChatsFunctionality", response.data.VIDEO_CHATS_ENABLED, { root: true });
    }

    if (
      response.data.VIDEO_CHATS_ENABLED &&
      !response.data.VIDEO_MULTI_PARTY &&
      rootState.videoChat.activeVideoChat &&
      rootState.videoChat.activeVideoChat.videoChatType === VIDEO_CHAT_TYPES.MANY_TO_MANY
    ) {
      await dispatch("videoChat/leaveVideoCall");
    }
    commit(
      "updateDigitalEventRoomInfo",
      {
        roomUUID: uuid,
        data: response.data,
        activeRoom: getters.activeRoom
      },
      { root: true }
    );

    return response.data;
  },

  /**
   * Get User Roles
   * @param commit {Function}
   * @param getters {Object}
   * @return {Promise<null>}
   */
  async getRoomAssignments({ commit, getters }) {
    let response = await evessioAPI.getAssignments({
      roomUUID: getters.activeRoom.uuid
    });

    commit("setRoomAssignments", response.data);

    return response.data;
  },

  /**
   * Update DigitalEventTableAssignment
   * @param commit {Function}
   * @param getters {Object}
   * @param data {Object} Dict with assignmentId and update data
   * @return {Promise<void>}
   */
  async updateAssignment({ commit, rootGetters }, data) {
    let response = await evessioAPI.updateAssignment(data);
    commit("updateAssignment", {
      assignmentId: rootGetters["users/userAssignment"].id,
      data: response.data
    });
    return response.data.assignment;
  },

  /**
   * Get room state
   * @param state
   * @param commit
   * @param dispatch
   * @return {Promise<void>}
   */
  async getRoomState({ state, commit, dispatch, rootGetters, rootState }) {
    let response = await awsRestAPI.getRoomState({
      updatedAt: state.deStateUpdatedAt
    });
    let users = response.data.users;
    const currentUserGroupUpdated =
      rootGetters["users/currentUser"] &&
      rootGetters["tables/userTable"] &&
      Boolean(
        users.find(
          user =>
            (user.userUUID === rootGetters["users/currentUser"].userUUID ||
              user.userUUID === rootGetters["users/currentUser"].user_uuid) &&
            user.tableUUID !== rootGetters["tables/userTable"].uuid
        )
      );
    // for old users that don't have ban fields
    response.data.users.forEach(user => {
      if (!Object.prototype.hasOwnProperty.call(user, "isBanned")) {
        user.isBanned = false;
        user.bannedBy = "";
      }
    });

    let usersUpdatedAt = users.map(user => new Date(user.userUpdatedAt));
    let maxUpdatedAt = new Date(Math.max.apply(null, usersUpdatedAt));
    commit("setRoomState", {
      users: users,
      updatedAt: maxUpdatedAt.toISOString()
    });
    if (currentUserGroupUpdated) {
      // Update user assignments if user table was changed
      await dispatch("getRoomAssignments");
    }

    /** TODO: implement kick logic on user move on backend */
    if (rootState.videoChat.activeVideoChat) {
      rootGetters["videoChat/videoChatMembers"].forEach(member => {
        rootGetters["tables/publicTableUsers"].forEach(publicUser => {
          if (member.userUUID === publicUser.userUUID) {
            commit("videoChat/deleteVideoChatMember", publicUser.userUUID, { root: true });

            if (rootGetters["videoChat/videoChatMembers"].length <= 1) {
              dispatch("videoChat/closeActiveVideoSession", null, { root: true });
            }
          }
        });
      });
    }

    return response.data;
  },

  /**
   * Process assignments merge/move
   * @param getters {Object}
   * @param dispatch {Function}
   * @return {Promise<void>}
   */
  async mergeAssignments({ dispatch, rootGetters }) {
    let oldTableUUID = rootGetters["tables/userTable"].uuid;

    await dispatch("getRoomAssignments");

    let newTableUUID = rootGetters["tables/userTable"].uuid;

    if (oldTableUUID !== newTableUUID) {
      await dispatch("chat/getTableMessages", null, { root: true });
    }
  },

  /**
   * Send change room request
   * @param dispatch {Function}
   * @param rootGetters {Object}
   * @param roomUUID {String}
   */
  async changeRoom({ commit }, { slug, enterRoomUUID }) {
    commit("setActiveRoom", null);
    let response = await awsRestAPI.changeRoom({ enterRoomUUID });
    if (response.status === 200) {
      commit("setActiveRoom", slug);
    }
  },
  /**
   * Get multiple rooms
   * @param commit {Function}
   * @param event_uuid {String}
   */
  async getRooms({ commit }, event_uuid) {
    let response = await evessioAPI.getRooms(event_uuid);
    commit("setRooms", response.data.data);
  }
};
